import { useState } from "react";
import { Plus, Search, Pencil, Trash2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable, Column } from "@/components/shared/DataTable";
import { Pagination } from "@/components/shared/Pagination";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { useEmployees, useDeleteEmployee, useUpdateEmployeeStatus } from "@/hooks/useEmployees";
import { useDebounce } from "@/hooks/useDebounce";
import { Employee } from "@/api/employee.api";
import { EmployeeFormDialog } from "./EmployeeFormDialog";
import { EmployeeDetailDialog } from "./EmployeeDetailDialog";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";

const PAGE_SIZE = 10;
type StatusFilter = "" | "ACTIVE" | "INACTIVE" | "ON_LEAVE";

const STATUS_STYLES: Record<Employee["status"], string> = {
  ACTIVE: "bg-success/15 text-success",
  INACTIVE: "bg-muted text-muted-foreground",
  ON_LEAVE: "bg-accent/15 text-accent-foreground",
};

export function EmployeesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("");
  const debouncedSearch = useDebounce(search, 350);

  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);

  const { toast } = useToast();
  const { data, isLoading } = useEmployees({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    status: status || undefined,
  });
  const deleteMutation = useDeleteEmployee();
  const statusMutation = useUpdateEmployeeStatus();

  const handleStatusToggle = async (employee: Employee) => {
    const next = employee.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await statusMutation.mutateAsync({ id: employee.id, status: next });
      toast({ title: `${employee.fullName} marked ${next.toLowerCase()}`, variant: "success" });
    } catch {
      toast({ title: "Could not update status", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deletingEmployee) return;
    try {
      await deleteMutation.mutateAsync(deletingEmployee.id);
      toast({ title: "Employee removed", variant: "success" });
      setDeletingEmployee(null);
    } catch (err) {
      const message = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      toast({ title: "Could not remove employee", description: message, variant: "destructive" });
    }
  };

  const columns: Column<Employee>[] = [
    { key: "name", header: "Name", render: (e) => <span className="font-medium">{e.fullName}</span> },
    { key: "designation", header: "Role", render: (e) => e.designation ?? "—" },
    { key: "phone", header: "Phone", render: (e) => <span className="font-data">{e.phone}</span> },
    { key: "email", header: "Email", render: (e) => e.user.email },
    { key: "active", header: "Active jobs", render: (e) => <span className="font-data">{e.activeJobs}</span> },
    { key: "completed", header: "Completed", render: (e) => <span className="font-data">{e.completedJobs}</span> },
    {
      key: "status",
      header: "Status",
      render: (e) => (
        <button
          onClick={(ev) => {
            ev.stopPropagation();
            handleStatusToggle(e);
          }}
          className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_STYLES[e.status])}
        >
          {e.status}
        </button>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (e) => (
        <div className="flex justify-end gap-1" onClick={(ev) => ev.stopPropagation()}>
          <Button variant="ghost" size="icon" onClick={() => setViewingEmployee(e)} aria-label="View">
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setEditingEmployee(e);
              setFormOpen(true);
            }}
            aria-label="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeletingEmployee(e)}
            aria-label="Delete"
            className="hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Employees</h1>
          <p className="text-sm text-muted-foreground">Manage technician accounts and track their workload.</p>
        </div>
        <Button
          onClick={() => {
            setEditingEmployee(null);
            setFormOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add employee
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, email..."
            className="pl-9"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className="flex h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as StatusFilter);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ON_LEAVE">On leave</option>
        </select>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          rowKey={(e) => e.id}
          onRowClick={(e) => setViewingEmployee(e)}
          emptyMessage="No employees yet. Add your first technician to get started."
        />
        <Pagination page={page} limit={PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
      </div>

      <EmployeeFormDialog open={formOpen} onOpenChange={setFormOpen} employee={editingEmployee} />
      <EmployeeDetailDialog
        open={Boolean(viewingEmployee)}
        onOpenChange={(open) => !open && setViewingEmployee(null)}
        employee={viewingEmployee}
      />
      <ConfirmDialog
        open={Boolean(deletingEmployee)}
        onOpenChange={(open) => !open && setDeletingEmployee(null)}
        title="Remove employee?"
        description={`This will deactivate ${deletingEmployee?.fullName ?? "this employee"}'s account and remove them from your records. Employees with active jobs assigned can't be removed until those jobs are reassigned.`}
        confirmLabel="Remove"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
